import { useEffect, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { CaretLeft, CaretRight, Info } from "@phosphor-icons/react";

const monthLabel = (m) => {
    const [y, mm] = m.split("-").map(Number);
    return new Date(y, mm - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

const shiftMonth = (m, delta) => {
    const [y, mm] = m.split("-").map(Number);
    const d = new Date(y, mm - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function GstSetOff() {
    const [month, setMonth] = useState(null); // null until we resolve latest-with-data
    const [data, setData] = useState(null);
    const [tds, setTds] = useState(null);
    const [challans, setChallans] = useState([]);
    const [tdsOut, setTdsOut] = useState(null);
    const [statutory, setStatutory] = useState(null);
    const [busy, setBusy] = useState(false);

    // Resolve the default month on first render — jump to the latest month that
    // actually has journal entries, otherwise the page opens empty for founders
    // logging in on a fresh 1st.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/reports/gst-set-off/latest-month");
                setMonth(data?.month || today());
            } catch {
                setMonth(today());
            }
        })();
    }, []);

    const load = async (m) => {
        setBusy(true);
        try {
            const [gst, tdsRep, ch, tdsOutstanding, stat] = await Promise.all([
                api.get("/reports/gst-set-off", { params: { month: m }}),
                api.get(`/reports/tds/${m}`).catch(() => ({ data: null })),
                api.get("/challans", { params: { limit: 200 }}).catch(() => ({ data: [] })),
                api.get("/challans/tds/outstanding").catch(() => ({ data: null })),
                api.get("/compliance/statutory-outstanding").catch(() => ({ data: null })),
            ]);
            setData(gst.data);
            setTds(tdsRep.data);
            setChallans(ch.data || []);
            setTdsOut(tdsOutstanding.data);
            setStatutory(stat.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    useEffect(() => { if (month) load(month); }, [month]);

    const cash = data?.cash_to_pay || {};
    const inputs = data?.inputs || {};
    const outputs = data?.outputs || {};
    const itc = data?.itc_carry_forward || {};
    const so = data?.set_off || {};
    const rcm = data?.rcm || {};
    const rcmNetCash = Math.max(0, (rcm.total_payable || 0) - (rcm.total_itc_claim || 0));

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Compliance"
                sub="GST (normal & RCM), TDS, PF and Professional Tax — everything you owe the government this month."
                right={
                    <div className="flex items-center gap-2" data-testid="gst-month-nav">
                        <Button variant="outline" size="sm" disabled={!month} onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid="gst-prev">
                            <CaretLeft size={14} />
                        </Button>
                        <div className="text-sm font-serif px-3" data-testid="gst-month-label">{month ? monthLabel(month) : "Loading…"}</div>
                        <Button variant="outline" size="sm" disabled={!month} onClick={() => setMonth((m) => shiftMonth(m, 1))} data-testid="gst-next">
                            <CaretRight size={14} />
                        </Button>
                    </div>
                }
            />
            <div className="p-4 sm:p-8 space-y-6">
                {busy && !data ? <div className="text-stone-500">Loading…</div> : null}

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="gst-cash-cards">
                    <StatCard label="Cash to pay — CGST" value={cash.cgst} tone="rose" testid="cash-cgst" />
                    <StatCard label="Cash to pay — SGST" value={cash.sgst} tone="rose" testid="cash-sgst" />
                    <StatCard label="Cash to pay — IGST" value={cash.igst} tone="rose" testid="cash-igst" />
                    <StatCard label="Total cash to pay" value={cash.total} tone="dark" bold testid="cash-total" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="input-card">
                        <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>Input tax (from purchases)</div>
                        <table className="se-table">
                            <tbody>
                                <TaxRow label="Input CGST" amount={inputs.cgst} />
                                <TaxRow label="Input SGST" amount={inputs.sgst} />
                                <TaxRow label="Input IGST" amount={inputs.igst} />
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="output-card">
                        <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>Output tax (from sales)</div>
                        <table className="se-table">
                            <tbody>
                                <TaxRow label="Output CGST" amount={outputs.cgst} />
                                <TaxRow label="Output SGST" amount={outputs.sgst} />
                                <TaxRow label="Output IGST" amount={outputs.igst} />
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="setoff-card">
                    <div className="px-5 py-3 border-b font-serif text-lg flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                        <span>Set-off workings</span>
                        <span className="text-xs text-stone-500">
                            <Info size={13} className="inline mr-1" />
                            IGST credit is applied to IGST first, then CGST, then SGST.
                        </span>
                    </div>
                    <table className="se-table">
                        <thead>
                            <tr>
                                <th>Liability</th>
                                <th>Amount</th>
                                <th>Paid from CGST</th>
                                <th>Paid from SGST</th>
                                <th>Paid from IGST</th>
                                <th>Net payable</th>
                            </tr>
                        </thead>
                        <tbody>
                            <SetoffRow name="IGST" gross={so.igst_liability_paid_from?.gross} fromC={so.igst_liability_paid_from?.cgst} fromS={so.igst_liability_paid_from?.sgst} fromI={so.igst_liability_paid_from?.igst} cash={cash.igst} />
                            <SetoffRow name="CGST" gross={so.cgst_liability_paid_from?.gross} fromC={so.cgst_liability_paid_from?.cgst} fromI={so.cgst_liability_paid_from?.igst} cash={cash.cgst} />
                            <SetoffRow name="SGST" gross={so.sgst_liability_paid_from?.gross} fromS={so.sgst_liability_paid_from?.sgst} fromI={so.sgst_liability_paid_from?.igst} cash={cash.sgst} />
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-4" data-testid="itc-cards">
                    <StatCard label="ITC c/f — CGST" value={itc.cgst} tone="teal" testid="itc-cgst" />
                    <StatCard label="ITC c/f — SGST" value={itc.sgst} tone="teal" testid="itc-sgst" />
                    <StatCard label="ITC c/f — IGST" value={itc.igst} tone="teal" testid="itc-igst" />
                </div>

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid="rcm-card">
                    <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                        <div>
                            <div className="font-serif text-lg">Reverse Charge (RCM)</div>
                            <div className="text-xs text-stone-500">
                                What you self-pay to the government under RCM, and the same amount you claim back as ITC.
                                Rates configurable at <a href="/settings" className="underline" style={{ color: "var(--se-brand)" }}>Settings</a>.
                            </div>
                        </div>
                    </div>
                    <table className="se-table" data-testid="rcm-table">
                        <thead>
                            <tr>
                                <th>Rate bucket</th>
                                <th>Typical use</th>
                                <th className="text-right">RCM payable</th>
                                <th className="text-right">RCM ITC claim</th>
                                <th className="text-right">Net cash impact</th>
                            </tr>
                        </thead>
                        <tbody>
                            <RcmRow label="@ 5%" hint="Transporter (GTA)" payable={rcm.payable_5} itc={rcm.itc_claim_5} />
                            <RcmRow label="@ 18%" hint="Rent, professional services" payable={rcm.payable_18} itc={rcm.itc_claim_18} />
                            <tr className="border-t" style={{ borderColor: "var(--se-border)", background: "rgba(15,23,42,0.02)" }}>
                                <td className="font-medium">Total</td>
                                <td className="text-xs text-stone-500">Aggregate for the month</td>
                                <td className="se-num font-medium" style={{ color: "#9f1239" }}>₹ {inr(rcm.total_payable || 0)}</td>
                                <td className="se-num font-medium" style={{ color: "#0f766e" }}>₹ {inr(rcm.total_itc_claim || 0)}</td>
                                <td className="se-num font-medium" style={{ color: rcmNetCash > 0 ? "#9f1239" : "#0f172a" }}>₹ {inr(rcmNetCash)}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="px-5 py-3 text-[11px] text-stone-500">
                        Net cash impact = RCM Payable − ITC claimed back. In a same-month claim scenario this is typically <b>zero</b>;
                        anything &gt; 0 means the payable was booked this month but the ITC will only be claimed next month.
                    </div>
                </div>

                {tds ? <TdsPanel tds={tds} /> : null}

                <StatutoryPanel statutory={statutory} />

                <OutstandingPanel tdsOut={tdsOut} gstCash={cash} month={month} />
                <ChallansHistoryPanel challans={challans} />
            </div>
        </>
    );
}

function TdsPanel({ tds }) {
    const total = tds.grand_total || 0;
    return (
        <div className="bg-white border rounded-md mt-6" style={{ borderColor: "var(--se-border)" }} data-testid="tds-panel">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div>
                    <div className="font-serif text-lg">TDS payable — {monthLabel(tds.month)}</div>
                    <div className="text-xs text-stone-500">
                        Due by <b>{tds.due_date}</b> (7th of the following month). Deposit via challan, then post it from the Bank page → click the <b>Challan</b> button on the government-payment debit line.
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Grand total</div>
                    <div className="se-num text-2xl" style={{ color: total > 0 ? "#9f1239" : "#0f172a" }}>
                        {total > 0 ? `₹ ${inr(total)}` : "—"}
                    </div>
                </div>
            </div>
            {tds.sections.length === 0 ? (
                <div className="p-8 text-center text-sm text-stone-500">No TDS deducted this month.</div>
            ) : (
                <div className="p-5 space-y-4 print:p-2">
                    {tds.sections.map((s) => (
                        <div key={s.section} className="border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid={`tds-section-${s.section}`}>
                            <div className="px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                                <div className="text-sm font-medium">{s.label}</div>
                                <div className="se-num text-sm font-medium" style={{ color: "#9f1239" }}>₹ {inr(s.total)}</div>
                            </div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-widest text-stone-500" style={{ borderBottom: "1px solid var(--se-border)" }}>
                                        <th className="px-4 py-2 text-left">Party</th>
                                        <th className="px-4 py-2 text-left">PAN</th>
                                        <th className="px-4 py-2 text-right">Deductions</th>
                                        <th className="px-4 py-2 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {s.parties.map((p) => (
                                        <tr key={`${p.pan || "no-pan"}-${p.party_name}`} className="border-t" style={{ borderColor: "var(--se-border)" }}>
                                            <td className="px-4 py-2">{p.party_name}</td>
                                            <td className="px-4 py-2 font-mono text-xs">{p.pan}</td>
                                            <td className="px-4 py-2 text-right text-xs text-stone-500">{p.count}</td>
                                            <td className="px-4 py-2 text-right se-num">₹ {inr(p.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                    <div className="pt-2 flex justify-end print:hidden">
                        <Button variant="outline" onClick={() => window.print()} data-testid="tds-print">
                            Print challan summary
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatutoryPanel({ statutory }) {
    if (!statutory) return null;
    const rows = statutory.rows || [];
    const total = statutory.total || 0;
    const buckets = statutory.totals_by_type || {};

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}
             data-testid="statutory-panel">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div>
                    <div className="font-serif text-lg">Statutory payables — PF & Professional Tax</div>
                    <div className="text-xs text-stone-500">
                        Live balance of every statutory-payable ledger. Pay via the government portal, then post the challan from the Bank page.
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Total outstanding</div>
                    <div className="se-num text-2xl" style={{ color: total > 0 ? "#9f1239" : "#0f172a" }}
                         data-testid="statutory-total">
                        {total > 0 ? `₹ ${inr(total)}` : "—"}
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 p-5 print:p-2">
                <StatCard label="PF Payable"
                           value={buckets.pf}
                           tone={buckets.pf > 0 ? "rose" : "dark"}
                           testid="statutory-pf" />
                <StatCard label="Professional Tax Payable"
                           value={buckets.pt}
                           tone={buckets.pt > 0 ? "rose" : "dark"}
                           testid="statutory-pt" />
                <StatCard label="ESI Payable"
                           value={buckets.esi}
                           tone={buckets.esi > 0 ? "rose" : "dark"}
                           testid="statutory-esi" />
            </div>
            {rows.length === 0 ? (
                <div className="p-8 text-center text-sm text-stone-500">No statutory ledgers configured yet.</div>
            ) : (
                <table className="se-table" data-testid="statutory-table">
                    <thead>
                        <tr>
                            <th>Ledger</th>
                            <th className="text-right">Outstanding</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.ledger_id} data-testid={`statutory-row-${r.ledger_id}`}>
                                <td>
                                    <a href={`/ledger/${r.ledger_id}`}
                                       className="hover:underline"
                                       style={{ color: "var(--se-brand)" }}>
                                        {r.ledger_name}
                                    </a>
                                </td>
                                <td className="se-num text-right"
                                    style={{ color: r.outstanding > 0 ? "#9f1239" : "#0f172a" }}>
                                    {r.outstanding > 0 ? `₹ ${inr(r.outstanding)}` : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function StatCard({ label, value, tone = "dark", bold = false, testid }) {
    const bg = tone === "rose" ? "rgba(225, 29, 72, 0.05)"
             : tone === "teal" ? "rgba(20, 184, 166, 0.05)"
             : "#fff";
    const color = tone === "rose" ? "#9f1239"
                : tone === "teal" ? "#0f766e"
                : "#0f172a";
    return (
        <div className="border rounded-md px-3 sm:px-5 py-3 sm:py-4 min-w-0" style={{ borderColor: "var(--se-border)", background: bg }} data-testid={testid}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 truncate">{label}</div>
            <div
                className={`se-num text-lg sm:text-2xl mt-1 truncate ${bold ? "font-medium" : ""}`}
                style={{ color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                title={`₹ ${inr(value || 0)}`}
            >
                ₹ {inr(value || 0)}
            </div>
        </div>
    );
}

function TaxRow({ label, amount, warn }) {
    return (
        <tr>
            <td>{label}{warn ? <span className="ml-2 se-chip se-chip-warn text-[10px]">reclassify</span> : null}</td>
            <td className="se-num">₹ {inr(amount || 0)}</td>
        </tr>
    );
}

function SetoffRow({ name, gross = 0, fromC = 0, fromS = 0, fromI = 0, cash = 0 }) {
    return (
        <tr>
            <td className="font-medium">{name}</td>
            <td className="se-num" style={{ color: gross > 0 ? "#0f172a" : "#a8a29e" }}>{gross > 0 ? `₹ ${inr(gross)}` : "—"}</td>
            <td className="se-num text-stone-500">{fromC ? `₹ ${inr(fromC)}` : "—"}</td>
            <td className="se-num text-stone-500">{fromS ? `₹ ${inr(fromS)}` : "—"}</td>
            <td className="se-num text-stone-500">{fromI ? `₹ ${inr(fromI)}` : "—"}</td>
            <td className="se-num" style={{ color: cash > 0 ? "#9f1239" : "#0f172a" }}>{cash > 0 ? `₹ ${inr(cash)}` : "—"}</td>
        </tr>
    );
}

function RcmRow({ label, hint, payable = 0, itc = 0 }) {
    const net = Math.max(0, (payable || 0) - (itc || 0));
    return (
        <tr data-testid={`rcm-row-${label.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}>
            <td className="font-medium">{label}</td>
            <td className="text-xs text-stone-500">{hint}</td>
            <td className="se-num" style={{ color: payable > 0 ? "#9f1239" : "#a8a29e" }}>{payable > 0 ? `₹ ${inr(payable)}` : "—"}</td>
            <td className="se-num" style={{ color: itc > 0 ? "#0f766e" : "#a8a29e" }}>{itc > 0 ? `₹ ${inr(itc)}` : "—"}</td>
            <td className="se-num" style={{ color: net > 0 ? "#9f1239" : "#a8a29e" }}>{net > 0 ? `₹ ${inr(net)}` : "—"}</td>
        </tr>
    );
}

function OutstandingPanel({ tdsOut, gstCash, month }) {
    if (!tdsOut) return null;
    const sections = tdsOut.sections || [];
    const anyOwing = sections.some((s) => (s.outstanding || 0) > 0.5) || (gstCash?.total || 0) > 0.5;
    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="outstanding-panel">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div>
                    <div className="font-serif text-lg">Outstanding to government</div>
                    <div className="text-xs text-stone-500">Settle these from a bank line — open Bank statements → pick the debit → click <b>Challan</b>.</div>
                </div>
                {anyOwing ? (
                    <a href="/bank" className="text-xs font-medium underline" style={{ color: "var(--se-brand)" }}>
                        Go to Bank statements →
                    </a>
                ) : null}
            </div>
            <table className="se-table" data-testid="outstanding-table">
                <thead><tr><th>Head</th><th className="text-right">Outstanding</th></tr></thead>
                <tbody>
                    {sections.map((s) => (
                        <tr key={s.section} data-testid={`outstanding-tds-${s.section}`}>
                            <td>{s.label}</td>
                            <td className="se-num" style={{ color: (s.outstanding || 0) > 0.5 ? "#9f1239" : "#a8a29e" }}>
                                {(s.outstanding || 0) > 0.5 ? `₹ ${inr(s.outstanding)}` : "—"}
                            </td>
                        </tr>
                    ))}
                    <tr data-testid="outstanding-gst">
                        <td>GST payable — {monthLabel(month)}</td>
                        <td className="se-num" style={{ color: (gstCash?.total || 0) > 0.5 ? "#9f1239" : "#a8a29e" }}>
                            {(gstCash?.total || 0) > 0.5 ? `₹ ${inr(gstCash.total)}` : "—"}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function ChallansHistoryPanel({ challans }) {
    if (!challans?.length) {
        return (
            <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="challans-history-empty">
                <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>Challans paid</div>
                <div className="p-8 text-center text-sm text-stone-500">
                    No challans posted yet. Post one from Bank statements → click <b>Challan</b> on the government-payment debit line.
                </div>
            </div>
        );
    }
    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="challans-history">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Challans paid</div>
                <div className="text-xs text-stone-500">{challans.length} on record</div>
            </div>
            <table className="se-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Period</th>
                        <th>Challan no.</th>
                        <th>Details</th>
                        <th className="text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {challans.map((c) => (
                        <tr key={c.id} data-testid={`challan-row-${c.id}`}>
                            <td className="font-mono text-xs">{c.payment_date || "—"}</td>
                            <td>
                                <span className="se-chip" style={{
                                    background: c.kind === "tds" ? "#eff6ff" : "#fdf4ff",
                                    color: c.kind === "tds" ? "#1e40af" : "#7c3aed",
                                }}>
                                    {c.kind === "tds" ? "TDS" : "GST"}
                                </span>
                            </td>
                            <td className="text-xs">{c.period_month || "—"}</td>
                            <td className="font-mono text-xs">{c.challan_no || "—"}</td>
                            <td className="text-xs text-stone-500">
                                {c.kind === "tds" ? (
                                    <>
                                        {c.section_label || c.section}
                                        {c.bsr_code ? <> · BSR {c.bsr_code}</> : null}
                                        {c.cin ? <> · CIN {c.cin}</> : null}
                                    </>
                                ) : (
                                    <>
                                        CGST ₹{inr(c.cash_split?.cgst || 0)} · SGST ₹{inr(c.cash_split?.sgst || 0)} · IGST ₹{inr(c.cash_split?.igst || 0)}
                                        {c.cpin ? <> · CPIN {c.cpin}</> : null}
                                    </>
                                )}
                            </td>
                            <td className="se-num font-medium" style={{ color: "#0f172a" }}>₹ {inr(c.amount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
